import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Film,
  Sparkles,
  Clapperboard,
  Camera,
  Layers,
  FileVideo,
  FileUp,
  LayoutGrid,
  Users,
  Play,
  ClipboardList,
  FileText,
  Languages,
  Check,
  ArrowRight,
  ArrowUpRight,
} from 'lucide-react';
import { API_BASE } from '../config/api';

/**
 * Landing — public marketing page rendered at `/` for logged-out visitors
 * (PHASE_1_BUILD_SPEC Task 5). Logged-in users never see this — App.jsx's
 * RootRoute renders the normal authenticated Home instead.
 *
 * - Product screenshots in /public/landing/ are real captures of the seeded
 *   demo workspace (backend/scripts/seed_demo.py — "The Coastline").
 * - Pricing is read live from GET /api/razorpay/config (workspace_prices_v2),
 *   never hardcoded a third time.
 * - Video slot: set LANDING_VIDEO_URL to a hosted mp4 / YouTube embed URL to
 *   replace the placeholder card with the real product tour.
 */

const LANDING_VIDEO_URL = ''; // e.g. 'https://www.youtube.com/embed/XXXX' — leave '' for placeholder

const formatINR = (value) => {
  if (value == null || Number.isNaN(Number(value))) return null;
  return `₹${Number(value).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
};

/* ------------------------------- sections ------------------------------- */

const Nav = () => (
  <header className="sticky top-0 z-40 border-b border-zinc-800/80 bg-zinc-950/80 backdrop-blur-md">
    <div className="max-w-6xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
      <a href="#top" className="flex items-center gap-2.5" aria-label="FrameFlow">
        <span className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center">
          <Film className="h-4.5 w-4.5 h-4 w-4 text-primary-foreground" />
        </span>
        <span className="font-display text-lg font-bold tracking-tight text-zinc-100">FrameFlow</span>
      </a>
      <nav className="hidden md:flex items-center gap-8 text-sm text-zinc-400">
        <a href="#how-it-works" className="hover:text-zinc-100 transition-colors">How it works</a>
        <a href="#features" className="hover:text-zinc-100 transition-colors">Features</a>
        <a href="#pricing" className="hover:text-zinc-100 transition-colors">Pricing</a>
      </nav>
      <div className="flex items-center gap-2">
        <Link
          to="/auth"
          className="px-3.5 py-2 text-sm font-medium text-zinc-300 hover:text-zinc-100 transition-colors"
          data-testid="landing-nav-signin"
        >
          Sign in
        </Link>
        <Link
          to="/auth"
          className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors"
          data-testid="landing-nav-cta"
        >
          Start free
        </Link>
      </div>
    </div>
  </header>
);

const Hero = () => (
  <section className="relative overflow-hidden" id="top">
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(234,88,12,0.18),transparent_55%)]" aria-hidden="true" />
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,rgba(234,88,12,0.10),transparent_60%)]" aria-hidden="true" />

    <div className="relative max-w-6xl mx-auto px-4 md:px-6 pt-16 md:pt-24 pb-10 text-center">
      <p className="inline-flex items-center gap-2 rounded-full border border-zinc-800 bg-zinc-900/60 px-3.5 py-1.5 text-[11px] font-semibold tracking-[0.18em] uppercase text-zinc-400">
        <Sparkles className="h-3.5 w-3.5 text-primary" />
        Script breakdown · stripboard · call sheets · daily reports
      </p>

      <h1 className="mt-6 font-display text-4xl sm:text-5xl md:text-6xl font-bold leading-[1.05] tracking-tight text-zinc-50 max-w-3xl mx-auto">
        The shoot-day operating system{' '}
        <span className="text-primary italic">for Indian productions.</span>
      </h1>

      <p className="mt-6 text-base md:text-lg text-zinc-400 leading-relaxed max-w-2xl mx-auto">
        Upload the script — English, Hindi, Telugu, Tamil, Final Draft or a censor-format
        PDF. FrameFlow breaks it down, builds the shoot days, sends the call sheet to every
        phone on the unit, and writes the producer&rsquo;s daily report at wrap.
      </p>

      <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
        <Link
          to="/auth"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-colors shadow-lg shadow-primary/20"
          data-testid="landing-hero-cta"
        >
          Start free — no credit card
          <ArrowRight className="h-4 w-4" />
        </Link>
        <a
          href="#pricing"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-xl border border-zinc-700 text-zinc-200 font-medium hover:bg-zinc-900 transition-colors"
        >
          See pricing
        </a>
      </div>

      {/* Production hierarchy chips — echoes the auth page */}
      <ul className="mt-10 flex flex-wrap items-center justify-center gap-2 text-[11px] font-semibold tracking-[0.16em] uppercase text-zinc-500">
        {[
          { icon: Layers, label: 'Workspace' },
          { icon: FileVideo, label: 'Film' },
          { icon: Clapperboard, label: 'Scene' },
          { icon: Camera, label: 'Shot' },
        ].map((step, idx, arr) => (
          <React.Fragment key={step.label}>
            <li className="inline-flex items-center gap-1.5 rounded-md border border-zinc-800 bg-zinc-900/50 px-2.5 py-1.5 text-zinc-300">
              <step.icon className="h-3 w-3 text-primary/80" />
              {step.label}
            </li>
            {idx < arr.length - 1 && (
              <span className="text-zinc-700 text-xs" aria-hidden="true">→</span>
            )}
          </React.Fragment>
        ))}
      </ul>
    </div>

    {/* Hero screenshot — real scene board from the seeded demo */}
    <div className="relative max-w-5xl mx-auto px-4 md:px-6 pb-16 md:pb-24">
      <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-2 shadow-2xl shadow-black/50">
        <div className="flex items-center gap-1.5 px-3 py-2">
          <span className="h-2.5 w-2.5 rounded-full bg-zinc-700" />
          <span className="h-2.5 w-2.5 rounded-full bg-zinc-700" />
          <span className="h-2.5 w-2.5 rounded-full bg-zinc-700" />
          <span className="ml-2 text-[10px] text-zinc-500 font-mono tracking-tight truncate">
            frameflow.cinerian.com / The Coastline / Scene Board
          </span>
        </div>
        <img
          src="/landing/scene-board.jpg"
          alt="FrameFlow scene board — scenes organized across Not Ready, Ready and Shot columns with per-scene shot coverage"
          className="rounded-xl w-full border border-zinc-800"
          loading="eager"
          data-testid="landing-hero-screenshot"
        />
      </div>
    </div>
  </section>
);

const VideoSlot = () => (
  <section className="max-w-4xl mx-auto px-4 md:px-6 py-14 md:py-20" aria-label="Product tour video">
    <div className="text-center mb-8">
      <h2 className="font-display text-2xl md:text-3xl font-bold tracking-tight text-zinc-100">
        Script in. Call sheet out.
      </h2>
      <p className="mt-2 text-zinc-400 text-sm md:text-base">
        Watch a screenplay become shoot days, a call sheet on a crew member&rsquo;s phone, and a daily report — in under two minutes.
      </p>
    </div>
    {LANDING_VIDEO_URL ? (
      <div className="aspect-video rounded-2xl overflow-hidden border border-zinc-800 shadow-2xl shadow-black/40">
        <iframe
          src={LANDING_VIDEO_URL}
          title="FrameFlow product tour"
          className="w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    ) : (
      <div
        className="aspect-video rounded-2xl border border-dashed border-zinc-700 bg-zinc-900/50 flex flex-col items-center justify-center gap-4 hover:border-primary/40 transition-colors"
        data-testid="landing-video-placeholder"
      >
        <span className="h-16 w-16 rounded-full bg-primary/15 border border-primary/30 flex items-center justify-center">
          <Play className="h-7 w-7 text-primary translate-x-0.5" />
        </span>
        <div className="text-center">
          <p className="text-zinc-200 font-medium">60-second product tour</p>
          <p className="text-zinc-500 text-sm mt-1">Coming soon — recording in progress</p>
        </div>
      </div>
    )}
  </section>
);

const STEPS = [
  {
    n: '01',
    title: 'Upload your screenplay',
    body:
      'PDF, Word, Final Draft, Fountain or plain text — in English, Hindi, Telugu, Tamil, Kannada or Malayalam. Indian "Scene 12 – Location" headings and print-shop censor scripts parse too. When the script changes, re-upload and FrameFlow shows the diff, keeping your shots and schedule.',
    img: '/landing/film-dashboard.jpg',
    alt: 'FrameFlow film dashboard with the script import dropzone front and center',
  },
  {
    n: '02',
    title: 'AI breaks it down, by department',
    body:
      'Cast, extras, props, wardrobe, vehicles, stunts, VFX and eight more categories per scene — plus a suggested shot list with type, angle and movement. Correct anything by hand; the AI never overwrites what you fixed. Your first 10 scenes are free.',
    img: '/landing/ai-breakdown.jpg',
    alt: 'FrameFlow AI Script Intelligence page showing per-scene characters, props, locations and key actions',
  },
  {
    n: '03',
    title: 'Schedule, call, shoot, report',
    body:
      'Auto-build shoot days on a colour-coded stripboard, check the day-out-of-days, send the call sheet as a private link to every phone on the unit, mark coverage on set even without signal, and hand the producer a daily production report at wrap.',
    img: '/landing/scene-board.jpg',
    alt: 'FrameFlow Kanban scene board with per-scene shot counts and coverage percentages',
  },
];

const HowItWorks = () => (
  <section id="how-it-works" className="max-w-6xl mx-auto px-4 md:px-6 py-14 md:py-20">
    <div className="text-center mb-12">
      <p className="text-[11px] font-semibold tracking-[0.22em] uppercase text-primary mb-3">How it works</p>
      <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-zinc-100">
        Three steps to a production-ready breakdown
      </h2>
    </div>

    <div className="space-y-16 md:space-y-24">
      {STEPS.map((step, idx) => (
        <div
          key={step.n}
          className={`flex flex-col ${idx % 2 === 1 ? 'md:flex-row-reverse' : 'md:flex-row'} items-center gap-8 md:gap-12`}
        >
          <div className="flex-1 min-w-0">
            <p className="font-mono text-sm text-primary mb-2">{step.n}</p>
            <h3 className="font-display text-2xl font-bold tracking-tight text-zinc-100 mb-3">{step.title}</h3>
            <p className="text-zinc-400 leading-relaxed">{step.body}</p>
          </div>
          <div className="flex-1 min-w-0 w-full">
            <img
              src={step.img}
              alt={step.alt}
              loading="lazy"
              className="rounded-xl border border-zinc-800 shadow-xl shadow-black/40 w-full"
            />
          </div>
        </div>
      ))}
    </div>
  </section>
);

const FEATURES = [
  { icon: Languages, title: 'Reads Indian scripts', body: 'Scene-N headings, Hindi/Telugu/Tamil/Kannada/Malayalam scene words, timecoded censor scripts and legacy-font PDFs — not just INT./EXT. sluglines.' },
  { icon: Sparkles, title: 'Breakdown by department', body: '14 element categories per scene, colour-coded the way a breakdown sheet is, editable by hand, and a shot list with camera fields.' },
  { icon: LayoutGrid, title: 'Stripboard & DOOD', body: 'Auto-build days by location and light, drag strips between days, and read the day-out-of-days that drives your cast cost. No AI credits.' },
  { icon: ClipboardList, title: 'Call sheets crews confirm', body: 'Generated from the day, per-person call times, sent as a private link that shows each person their own call first — and tells you who has confirmed.' },
  { icon: Clapperboard, title: 'On-set coverage mode', body: 'A phone view of today: tap shots as covered or pickup, note the take. Works through bad signal and syncs when it returns.' },
  { icon: FileText, title: 'Daily production report', body: 'Scenes done, shots covered, pickups owed, call and wrap slip, delays — assembled at wrap and emailed to the producer as a PDF.' },
];

const FeatureGrid = () => (
  <section id="features" className="border-y border-zinc-800/70 bg-zinc-900/30">
    <div className="max-w-6xl mx-auto px-4 md:px-6 py-14 md:py-20">
      <div className="text-center mb-12">
        <p className="text-[11px] font-semibold tracking-[0.22em] uppercase text-primary mb-3">Features</p>
        <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-zinc-100">
          Built around how films are actually made
        </h2>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {FEATURES.map((f) => (
          <div
            key={f.title}
            className="rounded-2xl border border-zinc-800 bg-zinc-950/60 p-6 hover:border-zinc-700 transition-colors"
          >
            <span className="h-10 w-10 rounded-xl bg-primary/15 flex items-center justify-center mb-4">
              <f.icon className="h-5 w-5 text-primary" />
            </span>
            <h3 className="font-display text-lg font-semibold text-zinc-100 mb-1.5">{f.title}</h3>
            <p className="text-sm text-zinc-400 leading-relaxed">{f.body}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const PLAN_STATICS = {
  free: {
    name: 'Free',
    blurb: 'For your first film.',
    features: [
      '1 active production',
      'AI breakdown for your first 10 scenes',
      'Stripboard, call sheets & crew links',
      'PDF exports with a FrameFlow footer',
      'Unlimited free viewers',
    ],
    cta: 'Start free',
  },
  pro: {
    name: 'Director',
    blurb: 'For working filmmakers.',
    features: [
      '3 active productions',
      'Unlimited AI breakdowns & re-analysis',
      'Clean PDFs — call sheets, DOOD, shot lists',
      'On-set coverage mode for the whole unit',
      '5 team seats + unlimited viewers',
      '50 GB storage',
    ],
    cta: 'Start with Director',
    highlight: true,
  },
  studio: {
    name: 'Studio',
    blurb: 'For production houses.',
    features: [
      '10 active productions',
      'Everything in Director',
      'Daily production reports to your producer',
      '15 team seats + unlimited viewers',
      'Advanced analytics & governance',
      '500 GB storage',
    ],
    cta: 'Start with Studio',
  },
};

const Pricing = () => {
  const [prices, setPrices] = useState(null); // workspace_prices_v2 block
  const [cycle, setCycle] = useState('monthly');

  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      try {
        const r = await axios.get(`${API_BASE}/razorpay/config`, { signal: controller.signal });
        setPrices(r.data?.workspace_prices_v2 || r.data?.workspace_prices || null);
      } catch {
        // pricing stays skeleton — CTAs still work
      }
    })();
    return () => controller.abort();
  }, []);

  const priceFor = (planKey) => {
    if (planKey === 'free') return { main: '₹0', sub: 'forever' };
    const block = prices?.[planKey];
    if (!block) return { main: null, sub: null };
    if (cycle === 'monthly') {
      return { main: formatINR(block.monthly), sub: 'per workspace / month' };
    }
    return {
      main: formatINR(block.monthly_equivalent_yearly),
      sub: `per month, billed yearly (${formatINR(block.yearly)}/yr)`,
    };
  };

  return (
    <section id="pricing" className="max-w-6xl mx-auto px-4 md:px-6 py-14 md:py-20">
      <div className="text-center mb-10">
        <p className="text-[11px] font-semibold tracking-[0.22em] uppercase text-primary mb-3">Pricing</p>
        <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-zinc-100">
          Free to start. Fair when you grow.
        </h2>
        <p className="mt-3 text-zinc-400 text-sm md:text-base">
          Your first film — with a real AI taste — costs nothing. Upgrade the workspace when the production does.
        </p>

        {/* Billing cycle toggle */}
        <div className="mt-6 inline-flex items-center rounded-xl border border-zinc-800 bg-zinc-900/60 p-1 text-sm" role="tablist" aria-label="Billing cycle">
          {['monthly', 'yearly'].map((c) => (
            <button
              key={c}
              role="tab"
              aria-selected={cycle === c}
              onClick={() => setCycle(c)}
              className={`px-4 py-1.5 rounded-lg font-medium capitalize transition-colors ${
                cycle === c ? 'bg-primary text-primary-foreground' : 'text-zinc-400 hover:text-zinc-200'
              }`}
              data-testid={`landing-pricing-cycle-${c}`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 items-stretch">
        {['free', 'pro', 'studio'].map((key) => {
          const plan = PLAN_STATICS[key];
          const { main, sub } = priceFor(key);
          return (
            <div
              key={key}
              className={`relative rounded-2xl border p-6 flex flex-col ${
                plan.highlight
                  ? 'border-primary/50 bg-primary/[0.06] shadow-lg shadow-primary/10'
                  : 'border-zinc-800 bg-zinc-900/40'
              }`}
              data-testid={`landing-plan-${key}`}
            >
              {plan.highlight && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full bg-primary text-primary-foreground text-[10px] font-bold tracking-[0.18em] uppercase">
                  Most popular
                </span>
              )}
              <h3 className="font-display text-xl font-bold text-zinc-100">{plan.name}</h3>
              <p className="text-sm text-zinc-500 mt-0.5">{plan.blurb}</p>
              <div className="mt-5 mb-1 min-h-[44px]">
                {main ? (
                  <span className="font-display text-4xl font-bold text-zinc-50" data-testid={`landing-price-${key}`}>
                    {main}
                  </span>
                ) : (
                  <span className="inline-block h-10 w-28 rounded-md bg-zinc-800 animate-pulse" aria-hidden="true" />
                )}
              </div>
              <p className="text-xs text-zinc-500 mb-6">{sub || '\u00A0'}</p>
              <ul className="space-y-2.5 text-sm text-zinc-300 flex-1">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5">
                    <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
              <Link
                to="/auth"
                className={`mt-7 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm transition-colors ${
                  plan.highlight
                    ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                    : 'border border-zinc-700 text-zinc-200 hover:bg-zinc-800'
                }`}
                data-testid={`landing-plan-cta-${key}`}
              >
                {plan.cta}
              </Link>
            </div>
          );
        })}
      </div>

      <p className="text-center text-xs text-zinc-600 mt-6">
        Prices in INR. Paid plans are billed per workspace via Razorpay. Cancel anytime.
      </p>
    </section>
  );
};

const FinalCta = () => (
  <section className="relative overflow-hidden border-t border-zinc-800/70">
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(234,88,12,0.14),transparent_60%)]" aria-hidden="true" />
    <div className="relative max-w-3xl mx-auto px-4 md:px-6 py-16 md:py-24 text-center">
      <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-zinc-50">
        Tomorrow&rsquo;s call sheet is already in your script.
      </h2>
      <p className="mt-4 text-zinc-400">
        Upload it, see the first 10 scenes broken down free, and have a shoot day built before you&rsquo;ve finished your coffee.
      </p>
      <Link
        to="/auth"
        className="mt-8 inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-colors shadow-lg shadow-primary/20"
        data-testid="landing-final-cta"
      >
        Start free — no credit card
        <ArrowRight className="h-4 w-4" />
      </Link>
    </div>
  </section>
);

const Footer = () => (
  <footer className="border-t border-zinc-800/70">
    <div className="max-w-6xl mx-auto px-4 md:px-6 py-10 flex flex-col md:flex-row items-center justify-between gap-4">
      <div className="flex items-center gap-2 text-zinc-500 text-sm">
        <span className="h-6 w-6 rounded-md bg-primary/80 flex items-center justify-center">
          <Film className="h-3.5 w-3.5 text-primary-foreground" />
        </span>
        <span className="font-display font-semibold text-zinc-300">FrameFlow</span>
        <span aria-hidden="true">·</span>
        <span>© {new Date().getFullYear()} FrameFlow. Precision engineered.</span>
      </div>
      <nav className="flex items-center gap-6 text-sm text-zinc-500">
        <Link to="/privacy" className="hover:text-zinc-200 transition-colors">Privacy</Link>
        <Link to="/terms" className="hover:text-zinc-200 transition-colors">Terms</Link>
        <Link to="/auth" className="inline-flex items-center gap-1 hover:text-zinc-200 transition-colors">
          Sign in <ArrowUpRight className="h-3.5 w-3.5" />
        </Link>
      </nav>
    </div>
  </footer>
);

/* --------------------------------- page --------------------------------- */

const Landing = () => {
  useEffect(() => {
    const prev = document.title;
    document.title = 'FrameFlow — Script breakdown, stripboard, call sheets and daily reports for Indian productions';
    return () => {
      document.title = prev;
    };
  }, []);

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 antialiased" data-testid="landing-page">
      <Nav />
      <main>
        <Hero />
        <VideoSlot />
        <HowItWorks />
        <FeatureGrid />
        <Pricing />
        <FinalCta />
      </main>
      <Footer />
    </div>
  );
};

export default Landing;
